import "./TemplateSaveDialog.scss";

import { Alert, DialogProps, Flex, Skeleton, Text } from "@fluentui/react-northstar";
import FormState, { validators } from "@shopify/react-form-state";
import { QuesionnaireTemplateCreateDTO } from "@teams/types/api";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";

import CoreConfirmationImage from "../../../../_shared/assets/confirmation-image-ok-mark.svg";
import { TextInputField } from "../../../../_shared/components/fields";
import { PollyDialog } from "../../../../_shared/components/PollyDialog";
import { FetchResult, isError } from "../../../../_shared/services/ChannelApi";
import { templatesApi } from "../../../../_shared/services/TemplatesApi";
import { useTeamsContext } from "../../../../_shared/state/TeamsContextProvider";

export type TemplateSaveFields = Omit<
  QuesionnaireTemplateCreateDTO,
  "templateName" | "templateDescription"
>;

type Props = {
  onSave: () => TemplateSaveFields;
  onClose: () => void;
  showViewTemplateButton?: boolean;
  updateTemlateId?: string;
  isGlobalTemplate?: boolean;
} & DialogProps;

export const TemplateSaveDialog: React.FC<Props> = ({
  onSave,
  onClose,
  showViewTemplateButton,
  updateTemlateId,
  isGlobalTemplate,
  ...dialogProps
}) => {
  const { teamsContext } = useTeamsContext();
  const history = useHistory();
  const [isSaved, setIsSaved] = useState<boolean>();
  const [error, setError] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [initialValues, setInitialValues] = useState<{ title: string; description: string }>({
    title: "",
    description: "",
  });

  useEffect(() => {
    if (!updateTemlateId || !dialogProps.open) {
      return;
    }

    (async () => {
      setIsLoading(true);
      const template = await templatesApi.getTemplate(teamsContext, updateTemlateId);
      setIsLoading(false);
      if (!isError(template)) {
        setInitialValues({
          title: template.templateName,
          description: template.templateDescription,
        });
      }
    })();
  }, [updateTemlateId, dialogProps.open, teamsContext]);

  return (
    <div className="template-save">
      <FormState
        validateOnSubmit
        onSubmit={async formData => {
          if (!formData.valid) {
            return;
          }

          setError(undefined);

          const template = onSave();

          const sendTemplate: QuesionnaireTemplateCreateDTO = {
            templateName: formData.fields.title.value,
            templateDescription: formData.fields.description.value ?? "",
            ...template,
          };

          let result: FetchResult<unknown>;
          if (!updateTemlateId || isGlobalTemplate) {
            result = await templatesApi.create(teamsContext, sendTemplate);
          } else {
            result = await templatesApi.update(teamsContext, updateTemlateId, sendTemplate);
          }

          if (!isError(result)) {
            setIsSaved(true);
          } else {
            setError(result.message);
          }
        }}
        initialValues={initialValues}
        validators={{
          title: [
            validators.required("Title is required"),
            validators.lengthMoreThan(2, "Title must be at least 2 characters"),
            validators.lengthLessThan(50, "Title must be less than 50 characters"),
          ],
          description: [
            validators.lengthLessThan(500, "Description must be less then 500 characters"),
          ],
        }}
      >
        {({ fields, submit, submitting }) => {
          const viewTemplateButton = showViewTemplateButton
            ? {
                content: "View Templates",
                onClick: () => {
                  history.push("/msteams/content/tab/templates");
                },
              }
            : undefined;

          return (
            <PollyDialog
              {...dialogProps}
              cancelButton={isSaved ? "Close" : "Cancel"}
              confirmButton={
                isSaved
                  ? viewTemplateButton
                  : {
                      content: "Save",
                      loading: submitting,
                      disabled: submitting,
                    }
              }
              onCancel={onClose}
              onConfirm={submit}
            >
              {isLoading && (
                <Skeleton animation="wave">
                  <Flex column gap="gap.smaller">
                    <Skeleton.Line width="20%" height="20px" />
                    <Skeleton.Input fluid />
                    <Skeleton.Line width="30%" height="20px" />
                    <Skeleton.Input fluid />
                  </Flex>
                </Skeleton>
              )}
              {!isLoading && isSaved && (
                <Flex column gap="gap.medium">
                  <img src={CoreConfirmationImage} alt="Success" width="100%" height={150} />
                  <Text
                    align="center"
                    content={`Saved as ${fields.title.value}`}
                    weight="bold"
                    size="large"
                  />
                </Flex>
              )}
              {!isLoading && !isSaved && (
                <form>
                  <TextInputField field={fields.title} label="Title" autoFocus required fluid />

                  <TextInputField
                    fluid
                    className="template-save__description"
                    multiple
                    label="Description (optional)"
                    field={fields.description}
                  />

                  {error && <Alert danger content={error} />}
                </form>
              )}
            </PollyDialog>
          );
        }}
      </FormState>
    </div>
  );
};
